import EventEmitter from 'eventemitter3';
import { isEqual } from 'lodash';
import { useEffect, useRef, useState } from 'react';

type State = {
  currentTime: number,
  duration: number,
  isLoading: boolean,
  isPlaying: boolean,
  isEnded: boolean,
  error?: Error,
};

class Player extends EventEmitter {
  state: State = {
    currentTime: 0,
    duration: 0,
    isLoading: false,
    isPlaying: false,
    isEnded: false,
    error: undefined,
  };

  private lastAnimationFrame = 0;

  private cacheState: State = {
    ...this.state,
  };

  setCurrentTime(time: number) {
    this.innerSetCurrentTime(time);
    this.endEmit();
  }

  play() {
    this.state.isEnded = false;
    this.state.isPlaying = true;
    this.endEmit();
  }

  pause() {
    this.state.isPlaying = false;
    cancelAnimationFrame(this.lastAnimationFrame);
    this.endEmit();
  }

  end() {
    this.pause();
    this.state.isEnded = true;
  }

  reset() {
    cancelAnimationFrame(this.lastAnimationFrame);
    this.state.isEnded = false;
    this.state.isPlaying = false;
    this.state.isLoading = false;
    this.state.error = undefined;
    this.state.duration = 0;
    this.innerSetCurrentTime(0);
    this.endEmit();
  }

  loadStart() {
    this.state.isLoading = true;
    this.endEmit();
  }

  loadEnd() {
    this.state.isLoading = false;
    this.endEmit();
  }

  setError(error?: Error) {
    this.state.isLoading = false;
    this.state.isPlaying = false;
    this.state.error = error;
    this.endEmit();
  }

  setDuration(duration: number) {
    this.state.duration = duration;
    this.endEmit();
  }

  private innerSetCurrentTime(currentTime: number) {
    const time = Math.max(Math.min(currentTime, this.state.duration), 0);

    this.state.currentTime = time;
    cancelAnimationFrame(this.lastAnimationFrame);
    if (!this.state.isPlaying || time >= this.state.duration) {
      return;
    }
    const startTime = Date.now();
    this.lastAnimationFrame = requestAnimationFrame(() => {
      if (!this.state.isPlaying) {
        return;
      }
      const currentTime = time + (Date.now() - startTime) / 1000;
      this.setCurrentTime(currentTime);
    });
  }

  private endEmit() {
    if (isEqual(this.state, this.cacheState)) {
      return;
    }
    this.cacheState = {
      ...this.state,
    };
    this.emit('change');
  }
}

export const usePlayerState = () => {
  const ref = useRef(new Player);

  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isEnded, setIsEnded] = useState(false);
  const [error, setError] = useState<Error | undefined>();

  useEffect(() => {
    const player = ref.current;

    const change = () => {
      setCurrentTime(player.state.currentTime);
      setDuration(player.state.duration);
      setIsLoading(player.state.isLoading);
      setIsPlaying(player.state.isPlaying);
      setIsEnded(player.state.isEnded);
      setError(player.state.error);
    };
    player.on('change', change);

    return () => {
      player.off('change', change);
    };
  }, []);

  return {
    currentTime,
    duration,
    isLoading,
    isPlaying,
    isEnded,
    error,
    ref,
  };
};
